import { a, getNetElements } from "../../../shared/globalState/a";
import { Block } from "../../../models/shapes/Block";
import { Line } from "../../../models/shapes/Line";
import { ElbowBlock } from "../../../models/resistances/ElbowBlock";
import { Tee } from "../../../models/resistances/Tee";

/**
 * Snapshot of calculation state used to detect when recalculation is needed
 */
export interface CalculationState {
  branchCount: number;
  branchIds: string[];
  branchElementCounts: Map<string, number>;
  terminalFlowRates: Map<string, number>;
  graphVersion: number;
}

/**
 * Create calculation state snapshot
 */
export function createCalculationState(): CalculationState | null {
  if (!a.branches || a.branches.length === 0) {
    return null;
  }

  const branchElementCounts = new Map<string, number>();
  const terminalFlowRates = new Map<string, number>();

  // Get all terminal elements and their flow rates
  const allNetElements = getNetElements() as (Line | Block | ElbowBlock | Tee)[];
  const terminalElements = allNetElements.filter((el): el is Block => el instanceof Block && el.checkIsTerminal());

  terminalElements.forEach((terminal) => {
    if (terminal.net && terminal.net.flowRateCmh !== undefined && terminal.net.flowRateCmh !== null) {
      terminalFlowRates.set(terminal.id, terminal.net.flowRateCmh);
    }
  });

  // Get branch structure
  a.branches.forEach((branch) => {
    branchElementCounts.set(branch.id, branch.elements.length);
  });

  // Get graph version (use branches count as a proxy for graph changes)
  const graphVersion = a.graphs ? a.graphs.length : 0;

  return {
    branchCount: a.branches.length,
    branchIds: a.branches.map((b) => b.id).sort(),
    branchElementCounts,
    terminalFlowRates,
    graphVersion,
  };
}

/**
 * Check if network calculations need to be updated
 */
export function needsCalculationUpdate(lastCalculationState: CalculationState | null, hasPendingChanges: boolean): boolean {
  // Always update if there are pending input changes
  if (hasPendingChanges) {
    return true;
  }

  // If no previous calculation state, we need to calculate
  if (!lastCalculationState) {
    return true;
  }

  // Check if branch structure changed
  if (!a.branches || a.branches.length !== lastCalculationState.branchCount) {
    return true;
  }

  // Check if branch IDs changed (branches added/removed/reordered)
  const currentBranchIds = a.branches.map((b) => b.id).sort();
  if (currentBranchIds.join(",") !== lastCalculationState.branchIds.join(",")) {
    return true;
  }

  // Check if branch element counts changed (elements added/removed from branches)
  for (const branch of a.branches) {
    const lastCount = lastCalculationState.branchElementCounts.get(branch.id);
    if (lastCount === undefined || lastCount !== branch.elements.length) {
      return true;
    }
  }

  // Check if graph structure changed
  const currentGraphVersion = a.graphs ? a.graphs.length : 0;
  if (currentGraphVersion !== lastCalculationState.graphVersion) {
    return true;
  }

  // Check if terminal flow rates changed (individual terminal edits)
  const allNetElements = getNetElements() as (Line | Block | ElbowBlock | Tee)[];
  const terminalElements = allNetElements.filter((el): el is Block => el instanceof Block && el.checkIsTerminal());

  for (const terminal of terminalElements) {
    const currentFlowRate = terminal.net?.flowRateCmh;
    const lastFlowRate = lastCalculationState.terminalFlowRates.get(terminal.id);

    // If flow rate changed or new terminal appeared
    if (currentFlowRate !== lastFlowRate) {
      return true;
    }
  }

  // Check if terminals were removed
  if (terminalElements.length !== lastCalculationState.terminalFlowRates.size) {
    return true;
  }

  return false;
}

